import type { JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { useWhatsAppTemplates } from '../../hooks/useWhatsAppTemplates.js'
import type { WhatsAppTemplate } from './types.js'

export interface TemplatePickerDialogProps {
  visible: boolean
  onHide: () => void
  onSelect: (template: WhatsAppTemplate) => void
}

/**
 * Task 7: lists APPROVED WhatsApp templates so an agent can pick one when a
 * conversation is outside the 24h window. Picking a template fires onSelect
 * (the composer then opens the variable form). §0.H: the dialog carries an
 * accessible name + aria-label.
 */
export default function TemplatePickerDialog(
  props: TemplatePickerDialogProps,
): JSX.Element {
  const { templates, loading, error } = useWhatsAppTemplates()
  const approved = templates.filter((t) => t.status === 'APPROVED')

  return (
    <Dialog
      visible={props.visible}
      onHide={props.onHide}
      header="Choose a WhatsApp template"
      aria-label="Choose a WhatsApp template"
      style={{ width: '36rem' }}
      modal
    >
      <div className="space-y-2 text-sm" aria-busy={loading}>
        {error && (
          <p role="alert" className="text-red-600">
            Failed to load templates: {error}
          </p>
        )}
        {loading && <p className="text-gray-500">Loading templates…</p>}
        {!loading && !error && approved.length === 0 && (
          <p className="text-gray-500">No approved templates available.</p>
        )}
        {!loading && approved.length > 0 && (
          <ul className="divide-y divide-gray-200">
            {approved.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start gap-1 px-2 py-2 text-left hover:bg-gray-50"
                  onClick={() => props.onSelect(t)}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{t.metaTemplateName}</span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                      {t.languageCode}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                      {t.category}
                    </span>
                  </span>
                  <span className="text-xs text-gray-600">{t.bodyText}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  )
}
